// Master Apps — configuração de apresentação do catálogo (porte de data.jsx).
import type { Application } from '@masterapps/shared';

// O App consumido pelo frontend: igual ao select do Drizzle, mas com as datas
// já serializadas como string (JSON sobre HTTP — o que o RPC retorna).
export type App = Omit<Application, 'createdAt' | 'updatedAt' | 'acessosColetadoEm'> & {
  createdAt: string;
  updatedAt: string;
  /**
   * Quando a coleta de acessos no Clerk rodou — timestamptz que chega como
   * string ISO pelo JSON, igual a `createdAt`/`updatedAt`. `null` = nunca
   * coletado / aplicação sem Clerk: a tela mostra "—", nunca 0.
   */
  acessosColetadoEm: string | null;
  /** SÓ no GET /:slug: responsável não consta ativo no RH → a ficha avisa. */
  ownerInactive?: boolean;
};

export interface AppTypeConfig {
  label: string;
  color: string;
  bg: string;
  dark: string;
}

export const APP_TYPES: Record<string, AppTypeConfig> = {
  'power-apps': { label: 'Power Apps', color: '#7c3aed', bg: '#ede9fe', dark: '#7c3aed22' },
  fullstack: { label: 'Fullstack', color: '#0891b2', bg: '#cffafe', dark: '#0891b222' },
  web: { label: 'App web', color: '#2563eb', bg: '#dbeafe', dark: '#2563eb22' },
  mobile: { label: 'Mobile', color: '#16a34a', bg: '#dcfce7', dark: '#16a34a22' },
  integracao: { label: 'Integração', color: '#ea580c', bg: '#ffedd5', dark: '#ea580c22' },
  rpa: { label: 'RPA', color: '#db2777', bg: '#fce7f3', dark: '#db277722' },
  api: { label: 'API / Backend', color: '#0d9488', bg: '#ccfbf1', dark: '#0d948822' },
  data: { label: 'Dados / BI', color: '#9333ea', bg: '#f3e8ff', dark: '#9333ea22' },
  infra: { label: 'Infra interna', color: '#475569', bg: '#f1f5f9', dark: '#47556922' },
};

/**
 * Tipos que o FORMULÁRIO de cadastro/edição não oferece mais, embora sigam
 * válidos no enum `app_type` do Postgres.
 *
 * `data` ("Dados / BI") saiu da lista SELECIONÁVEL por decisão de produto: as
 * aplicações analíticas não são cadastradas neste inventário. Verificado no
 * banco de produção — nenhuma das 42 aplicações usa `type = 'data'`, então não
 * há registro a migrar.
 *
 * A chave CONTINUA em `APP_TYPES` de propósito: o enum do banco não mudou, e a
 * exibição (chip do catálogo, coluna Tipo do inventário, CSV, deck) tem de saber
 * traduzir `data` para "Dados / BI" caso um dia apareça uma linha assim — sem
 * isso a tabela mostraria a chave crua (ou vazio). Ou seja: some da ESCOLHA,
 * permanece na LEITURA.
 */
export const NON_SELECTABLE_APP_TYPES: ReadonlySet<string> = new Set(['data']);

/**
 * Stacks pré-selecionadas quando o TIPO da aplicação é FULLSTACK — o padrão dos
 * monorepos fullstack da Masterboi (nocmaster, contracts-dashboard,
 * master-apps): o mesmo ferramental em todos, então o cadastro vira revisão em
 * vez de digitação. O formulário MESCLA esta lista às stacks já marcadas ao
 * trocar o tipo para fullstack (nada é removido nem duplicado); o usuário tira
 * o que a aplicação não usar. Nomes EXATOS das chaves de STACK_COLORS.
 */
export const FULLSTACK_DEFAULT_STACKS: readonly string[] = [
  'Bun',
  'Hono',
  'TypeScript',
  'React',
  'Vite',
  'Drizzle ORM',
  'Clerk',
  'Postgres',
  'Docker',
  'GitHub Actions',
];

export const STACK_COLORS: Record<string, string> = {
  React: '#61dafb',
  'Next.js': '#0a0a0b',
  'Node.js': '#3c873a',
  TypeScript: '#3178c6',
  Python: '#3776ab',
  FastAPI: '#009688',
  'Power Fx': '#742774',
  SharePoint: '#0078d4',
  Dataverse: '#742774',
  Flutter: '#02569b',
  Postgres: '#336791',
  'SQL Server': '#a91d22',
  Oracle: '#c74634',
  Docker: '#0db7ed',
  n8n: '#ea4b71',
  'Power BI': '#f2c811',
  '.NET': '#512bd4',
  Vue: '#41b883',
  Tailwind: '#06b6d4',
  Redis: '#dc382d',
  MongoDB: '#47a248',
  Laravel: '#ff2d20',
  PHP: '#777bb4',
  // Power Platform e Microsoft
  'Power Apps': '#742774',
  'Power Automate': '#0066ff',
  'Power Pages': '#0f6cbd',
  'Microsoft 365': '#d83b01',
  'Microsoft Forms': '#036c70',
  Excel: '#217346',
  Teams: '#6264a7',
  'Entra ID': '#0078d4',
  Azure: '#0089d6',
  // Frontend
  JavaScript: '#f7df1e',
  Vite: '#646cff',
  'shadcn/ui': '#18181b',
  'TanStack Query': '#ff4154',
  Zustand: '#bd7c4f',
  'React Router': '#f44250',
  Angular: '#dd0031',
  Svelte: '#ff3e00',
  // Backend
  Bun: '#fbf0df',
  Hono: '#e36002',
  'Drizzle ORM': '#c5f74f',
  Zod: '#3068b7',
  Clerk: '#6c47ff',
  'C#': '#68217a',
  Java: '#f89820',
  ADVPL: '#0f7ac0',
  // Mobile
  Dart: '#0175c2',
  'React Native': '#61dafb',
  Expo: '#000020',
  Kotlin: '#7f52ff',
  Swift: '#f05138',
  PWA: '#5a0fc8',
  // Banco de dados
  MySQL: '#4479a1',
  SQLite: '#003b57',
  // Integração e automação
  'TOTVS Protheus': '#004f9f',
  SAP: '#0faaff',
  WMS: '#64748b',
  'REST API': '#0d9488',
  SOAP: '#5c6bc0',
  Webhooks: '#0ea5e9',
  RabbitMQ: '#ff6600',
  BullMQ: '#d63447',
  'Power Automate Desktop': '#0b53ce',
  Selenium: '#43b02a',
  Playwright: '#2ead33',
  // Dados e BI
  'Power Query': '#00b294',
  DAX: '#d9a300',
  SQL: '#e38c00',
  Pandas: '#150458',
  Metabase: '#509ee3',
  Airflow: '#017cee',
  // Infraestrutura e DevOps
  Portainer: '#13bef9',
  Traefik: '#24a1c1',
  Nginx: '#009639',
  MinIO: '#c72e49',
  Kubernetes: '#326ce5',
  Git: '#f05032',
  'GitHub Actions': '#2088ff',
  SonarQube: '#4e9bcd',
  Grafana: '#f46800',
  Zabbix: '#d40000',
  'Uptime Kuma': '#5cdd8b',
  Linux: '#fcc624',
  'Windows Server': '#0078d6',
};

export interface StatusTypeConfig {
  label: string;
  color: string;
  bg: string;
}

/**
 * Situação da aplicação. `pausado` é o projeto iniciado e PARADO por decisão —
 * não é `dev` (ninguém está construindo), não é `manutencao` (não está no ar) e
 * não é `depreciado` (não foi descontinuado; pode voltar).
 *
 * O roxo dele é o MESMO valor do deck (`DECK_STATUS.pausado.color`), e não a
 * versão viva que as outras cinco cores usam aqui. É deliberado: nenhum roxo
 * vivo passa no teste de daltonismo das DUAS paletas ao mesmo tempo — os
 * candidatos claros encostam no azul de "Desenvolvimento" para quem tem
 * protanopia. O #9a348c passa nas duas, então a situação nova é a única que
 * aparece com a mesma cor no catálogo e na apresentação. Detalhe da conferência
 * no comentário de `DECK_STATUS` em `lib/deck.ts`.
 */
export const STATUS_TYPES: Record<string, StatusTypeConfig> = {
  prod: { label: 'Produção', color: '#16a34a', bg: '#dcfce7' },
  homolog: { label: 'Homologação', color: '#ca8a04', bg: '#fef9c3' },
  dev: { label: 'Desenvolvimento', color: '#2563eb', bg: '#dbeafe' },
  depreciado: { label: 'Depreciado', color: '#dc2626', bg: '#fee2e2' },
  manutencao: { label: 'Manutenção', color: '#ea580c', bg: '#ffedd5' },
  pausado: { label: 'Pausado', color: '#9a348c', bg: '#f6e4f2' },
};

// Variante do screenshot faux-UI (0=dashboard,1=lista,2=form,3=grid).
// O design deriva de `id.charCodeAt(4)%4`; aqui mapeamos por slug para
// reproduzir EXATAMENTE a distribuição do mock, com fallback determinístico
// (hash do slug) para apps novas cadastradas via formulário.
const VARIANT_BY_SLUG: Record<string, number> = {
  'apontamento-prod': 0,
  'portal-pecuarista': 0,
  'noc-master': 0,
  'checklist-veiculos': 0,
  'integracao-sap-wms': 0,
  conciliacao: 0,
  'api-rastreabilidade': 0,
  portainer: 0,
  registry: 1,
  acessos: 1,
  cardapio: 1,
};

export function screenshotVariant(slug: string | null | undefined): number {
  if (!slug) return 0;
  if (slug in VARIANT_BY_SLUG) return VARIANT_BY_SLUG[slug] as number;
  // Itera CODE POINTS (`for..of`), não unidades UTF-16: para slug ASCII o
  // resultado é idêntico ao antigo `charCodeAt`, e um caractere fora do BMP passa
  // a somar uma vez o code point em vez de duas metades de par surrogado.
  let h = 0;
  for (const char of slug) h = (h + (char.codePointAt(0) ?? 0)) % 4;
  return h;
}
